<template>
  <div>
    <v-container v-if="profile" class="pt-0">
      <v-row>
        <v-col class="pt-0">
          <span class="headline pb-1">{{$t('profile.title')}}</span>
        </v-col>
      </v-row>
      <v-row justify-start>
        <v-col cols="12" sm="4" class="pt-0 pb-0">
          <profile-avatar :profile="profile" />
          <v-btn
            id="change-avatar"
            :disabled="loading"
            v-if="$store.state.env.global.profileImageChangeAllowed === true"
          >{{$t('profile.change-image')}}</v-btn>
          <avatar-cropper
            trigger="#change-avatar"
            :labels="avatarCropperLabels"
            :upload-handler="onUploadAvatar"
          />
        </v-col>
        <v-col cols="12" sm="8">
          <v-container ma-0 pa-0>
            <v-row>
              <v-col cols="12" md="auto">
                <span class="title">{{profile.name}}</span>
                <verified-sign v-if="profile.verified" />
              </v-col>
              <v-col cols="auto">
                <v-btn id="change-username" :disabled="loading" @click="changeUsername">
                  <v-icon left>mdi-pencil</v-icon>
                  {{$t('profile.change-username')}}
                </v-btn>
                <change-username-dialog v-if="showChangeUsernameDialog" :visible="true" :initialUsername="profile.name" :closeAction="closeChangeUsernameDialog"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <span>{{$t('common.email-address')}}: {{profile.email}}</span>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="pa-0">
      <v-row class="no-gutters">
        <v-col cols="12" v-if="hasExternalAccount('twitch') || hasPermissionToLinkExtAccountDriver('twitch')">
          <socialite-manage :driver="'twitch'" :list="profile.externalAccounts"/>
        </v-col>
        <v-col cols="12" v-if="hasExternalAccount('youtube') || hasPermissionToLinkExtAccountDriver('youtube')">
          <socialite-manage :driver="'youtube'" :list="profile.externalAccounts"/>
        </v-col>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="11">
              <v-text-field
                  v-model="twitterHandle"
                  label="Twitter Username"
                  name="twitter"
                  filled
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-btn block depressed color="primary" height="56" @click="saveSocial"><v-icon>mdi-content-save</v-icon></v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="11">
              <v-text-field
                  v-model="instaHandle"
                  label="Instagram Username"
                  name="instagram"
                  filled
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-btn block depressed color="primary" height="56" @click="saveSocial"><v-icon>mdi-content-save</v-icon></v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="profile && isSupportAssociationFeatureEnabled()">
      <span class="subtitle-1">{{$t('supportAssociation.title')}}</span>
      <v-row>
        <v-col cols="12">
          <google-place-autocomplete
            data-test-id="support_association"
            v-model="supportAssociation"
            :label="$t('supportAssociation.select-label')"
          />
        </v-col>
        <v-col cols="12" class="pt-0" v-if="supportAssociation">
          <div class="secondary--text">
            <v-icon left small class="secondary--text">mdi-information-outline</v-icon>
            <span
              v-if="profile.premium === true"
              class="caption"
            >Dein Förderverein wird potentiell gesponsert, da Du ein Premium Account besitzt.</span>
            <span
              class="caption"
              v-else
            >Dein Förderverein wird aktuell nicht gesponsert. Hierzu benötigst Du ein Premium Account.</span>
          </div>
        </v-col>
        <v-col v-else cols="12" class="pt-0">
          <span
            class="caption"
          >{{$t('profile.club-not-listed-note')}}</span>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="profile">
      <v-form>
        <span class="subtitle-1">{{$t('profile.fullname-title')}}</span>
        <v-row>
          <v-col cols="12" class="pb-0">
            <v-text-field
              v-model="firstname"
              :disabled="loading"
              :label="$t('profile.firstname')"
              autocomplete="given-name"
            ></v-text-field>
            <v-text-field
              v-model="lastname"
              :disabled="loading"
              :label="$t('profile.lastname')"
              autocomplete="family-name"
            ></v-text-field>
          </v-col>
          <v-col cols="12" v-if="nameEdited">
            <v-btn class="primary" :disabled="loading" @click="saveName">
              <v-icon left>mdi-content-save</v-icon>{{$t('common.save')}}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pt-0">
            <div class="secondary--text">
              <v-icon left small class="secondary--text">mdi-information-outline</v-icon>
              <span
                class="caption"
              >{{$t('profile.name-not-shown-note')}}</span>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
    <v-container v-if="profile">
      <v-form>
        <span class="subtitle-1">{{$t('profile.personal-description')}}</span>
        <v-row>
          <v-col cols="12" class="pb-0">
            <v-textarea
              v-model="bio"
              name="input-bio"
              :disabled="loading"
              :counter="1000"
              :label="$t('profile.personal-description')"
              hint
              :rules="validationRules.bio"
            ></v-textarea>
          </v-col>
          <v-col cols="12" class="pt-0" v-if="profile.premium === false">
            <div class="secondary--text">
              <v-icon left small class="secondary--text">mdi-information-outline</v-icon>
              <span
                class="caption"
              >{{$t('profile.personal-description-note')}}</span>
            </div>
          </v-col>
          <v-col cols="12" class="pt-0" v-if="bioEdited">
            <v-btn class="primary" :disabled="loading" @click="saveBio">
              <v-icon left>mdi-content-save</v-icon>{{$t('common.save')}}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
    <v-container v-if="profile">
      <span class="subtitle-1">{{$t('profile.your-online-ids')}}</span>
      <v-row>
        <v-col>
          <online-ids/>
        </v-col>
      </v-row>
      <v-row v-if="!$store.state.env.global.isCoinsPayoutDisabled">
        <v-col>
          <v-btn @click="openManagePayoutAccountsDialog"><v-icon left>mdi-bank</v-icon>{{$t('managePayoutAccounts.title')}}</v-btn>
        </v-col>
        <manage-payout-accounts-dialog v-if="showPayoutAccountsDialog" :visible="showPayoutAccountsDialog" :closeAction="closeManagePayoutAccountsDialog"/>
      </v-row>
      <v-row class="align-center">
        <v-col cols="12">
          <v-card>
            <v-toolbar dense>
              <v-toolbar-title class="white--text">
                <span class="subtitle-1">{{$t('profile.more-options')}}</span>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                icon
                @click="moreOptionsCollapsed = false"
                v-if="moreOptionsCollapsed === true"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
              <v-btn
                icon
                @click="moreOptionsCollapsed = true"
                v-if="moreOptionsCollapsed === false"
              >
                <v-icon>mdi-chevron-up</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text v-show="!moreOptionsCollapsed">
              <v-container class="pa-0">
                <v-row>
                  <v-col cols="12" v-if="hasPermissionToForceAdvertising()" class="pb-4 pt-0">
                    <v-switch
                      v-model="forceAdvertising"
                      label="Werbung auf diesem Gerät forcieren"
                      color="warning"
                      hide-details
                    ></v-switch>
                  </v-col>
                  <v-col cols="12" v-if="hasPermissionToHideCoins()" class="pb-4 pt-0">
                    <v-switch
                      v-model="hideCoinFeature"
                      label="Coin-Menü auf diesem Gerät verstecken"
                      color=""
                      hide-details
                    ></v-switch>
                  </v-col>
                  <v-col cols="12">
                    <user-deletion-button />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import GooglePlaceAutocomplete from "@/widgets/GooglePlaceAutocomplete";
import {
  ACTION_LOAD_PROFILE,
  MUTATION_PROFILE_LOADED,
  MUTATION_SET_FORCE_ADVERTISING,
  MUTATION_HIDE_COIN_FEATURE
} from "@/plugins/store";
import platformMixins from "@/mixins/platformMixins.js";
import VerifiedSign from "@/widgets/VerifiedSign";
import AvatarCropper from "vue-avatar-cropper";
import ProfileAvatar from "@/widgets/ProfileAvatar";
import UserDeletionButton from "@/widgets/UserDeletionButton";
import envMixin from "@/mixins/envMixin";
import OnlineIds from "@/widgets/OnlineIds";
import SocialiteManage from '@/widgets/SocialiteManage.vue';
import ChangeUsernameDialog from "@/widgets/ChangeUsernameDialog";
import ManagePayoutAccountsDialog from "@/widgets/payout/ManageAccountsDialog"

export default {
  name: "Profile",
  mixins: [platformMixins, envMixin],
  components: {
    VerifiedSign,
    GooglePlaceAutocomplete,
    AvatarCropper,
    ProfileAvatar,
    UserDeletionButton,
    OnlineIds,
    SocialiteManage,
    ChangeUsernameDialog,
    ManagePayoutAccountsDialog,
  },
  data: function() {
    return {
      loading: false,
      isCoinsPayoutDisabled: false,
      supportAssociation: null,
      avatarCropperLabels: { submit: this.$t('common.ok'), cancel: this.$t('common.cancel') },
      firstname: null,
      lastname: null,
      bio: null,
      moreOptionsCollapsed: true,
      validationRules: {
        required: [v => !!v || this.$t('common.mandatory-field')],
        bio: [v => !v || v.length <= 1000 || this.$t('common.max-n-characters', {n: 1000})]
      },
      forceAdvertising: undefined,
      hideCoinFeature: undefined,
      showChangeUsernameDialog: false,
      showPayoutAccountsDialog: false,
      twitterHandle: '',
      instaHandle: '',
    }
  },

  watch: {
    supportAssociation: function(val) {
      if (
        val &&
        this.$store.state.profile.supportAssociation &&
        val.place_id == this.$store.state.profile.supportAssociation.place_id
      ) {
        return; // Ignore when the place_id did not change
      }
      if (!val && !this.$store.state.profile.supportAssociation) {
        return; // Ignore unsetting support association
      }
      console.log("Support association has changed", val);
      let data = { support_association_place_id: val ? val.place_id : null };
      this.$api
        .patchProfile(data)
        .then(res => {
          this.$store.commit(MUTATION_PROFILE_LOADED, res.data);

        })
        .catch(error => {
          this.$common.onError(error)
        });
    },
    forceAdvertising: function(val, oldVal) {
      if (oldVal !== undefined) {
        this.$store.commit(MUTATION_SET_FORCE_ADVERTISING, val)
      }
    },
    hideCoinFeature: function(val, oldVal) {
      if (oldVal !== undefined) {
        this.$store.commit(MUTATION_HIDE_COIN_FEATURE, val)
      }
    },
    '$route': function () {
      this.updateManagePayoutAccountDialogVisibility()
    }
  },

  computed: {
    profile: function() {
      return this.$store.state.profile;
    },
    bioEdited: function() {
      return !this.stringEquals(this.bio, this.$store.state.profile.bio);
    },
    nameEdited: function() {
      return !(
        this.stringEquals(
          this.firstname,
          this.$store.state.profile.firstname
        ) &&
        this.stringEquals(this.lastname, this.$store.state.profile.lastname)
      );
    }
  },

  mounted() {
    this.resolveProfile();
    this.supportAssociation = this.$store.state.profile.supportAssociation;
    this.bio = this.$store.state.profile.bio;
    this.firstname = this.$store.state.profile.firstname;
    this.twitterHandle = this.$store.state.profile.twitter_handle;
    this.instaHandle = this.$store.state.profile.insta_handle;
    this.lastname = this.$store.state.profile.lastname;
    this.forceAdvertising = this.$store.state.advertising.forceAdvertising == true;
    this.hideCoinFeature = this.$store.state.coinSystem.hideCoinFeature == true;
    this.updateManagePayoutAccountDialogVisibility()
  },

  methods: {
    stringEquals(a, b) {
      a = a ? a.trim() : null;
      if (a == "" ? null : a);
      b = b ? b.trim() : null;
      if (b == "" ? null : b);
      return a === b;
    },
    resolveProfile() {
      this.error = undefined;
      // console.log("Loading profile...");
      this.loading = true;
      this.$store.dispatch(ACTION_LOAD_PROFILE).finally(() => {
        this.loading = false;
      });
    },
    hasExternalAccount(driver) {
      return this.profile && this.profile.externalAccounts && this.profile.externalAccounts.find((a) => {return a.driver == driver})
    },
    saveSocial() {
      let data = {
        twitter_handle: this.twitterHandle,
        insta_handle: this.instaHandle,
      }
      this.loading = true;
      this.$api
          .patchProfile(data)
          .then(res => {
            this.loading = false;
            this.$store.commit(MUTATION_PROFILE_LOADED, res.data);
          })
          .catch(error => {
            this.loading = false;
            this.$common.onError(error)
          });
    },
    saveBio() {
      let data = { bio: this.bio };
      this.loading = true;
      this.$api
        .patchProfile(data)
        .then(res => {
          this.loading = false;
          this.$store.commit(MUTATION_PROFILE_LOADED, res.data);
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
    },
    saveName() {
      let data = {
        firstname: this.firstname,
        lastname: this.lastname
      };
      this.loading = true;
      this.$api
        .patchProfile(data)
        .then(res => {
          this.loading = false;
          this.$store.commit(MUTATION_PROFILE_LOADED, res.data);
        })
        .catch(error => {
          this.loading = false;
          this.$common.onError(error)
        });
    },
    changeUsername() {
      this.showChangeUsernameDialog = true;
    },
    closeChangeUsernameDialog() {
      this.showChangeUsernameDialog = false;
    },
    saveProfile() {
      console.log("N/A");
    },
    onUploadAvatar(cropperJS) {
      let canvas = cropperJS.getCroppedCanvas({ width: 600, height: 600 });
      canvas.toBlob(blob => {
        const formData = new FormData();
        formData.append("file", blob);
        this.loading = true;
        this.$api
          .uploadUserAvatar(formData)
          .then(res => {
            this.loading = false;
            this.$store.commit(MUTATION_PROFILE_LOADED, res.data);
          })
          .catch(error => {
            this.loading = false;
            this.$common.onError(error)
          });
      });
    },
    openManagePayoutAccountsDialog() {
      this.showPayoutAccountsDialog = true
      this.$router.replace({ query: { ...this.$route.query, managePayoutAccounts: 'true' } });
    },
    closeManagePayoutAccountsDialog() {
      this.showPayoutAccountsDialog = false
      this.$router.replace({ query: { ...this.$route.query, managePayoutAccounts: undefined } });
    },
    updateManagePayoutAccountDialogVisibility() {
      this.showPayoutAccountsDialog = (this.$route.query.managePayoutAccounts == 'true' ? true : false)
    }
  }
};
</script>

<style>
.avatar-cropper-footer {
  background-color: #272727;
}

.avatar-cropper-btn:nth-child(1) {
  background-color: var(--v-error-base) !important;
}

.avatar-cropper-btn:nth-child(2) {
  background-color: var(--v-primary-base) !important;
}

.cropper-line {
  background-color: var(--v-accent-base) !important;
}

.cropper-view-box {
  outline: 1px solid #39f;
  outline-color: var(--v-accent-base) !important;
}

.cropper-point {
  background-color: var(--v-accent-base) !important;
}

.avatar-cropper-mark {
  display: none;
}
</style>
